import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Send, Loader2, Bot, User, Database, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useAiStream } from '@/api/hooks/useAiStream';
import { useAiAssistantStore } from '@/stores/aiAssistantStore';
import { useFeature } from '@/contexts/FeatureFlagContext';

interface ChatTurn {
  role: 'user' | 'assistant';
  text: string;
  tools?: string[];
}

/** Human-readable description of the current route, sent as context so "explain this" works. */
function describeRoute(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)[0] ?? 'dashboard';
  const map: Record<string, string> = {
    dashboard: 'the Dashboard — KPI cards (assets, incidents, inventories, pallets) and trend charts',
    assets: 'the Assets list',
    inventory: 'Inventory counts and reconciliation',
    incidents: 'Incidents',
    approvals: 'Approvals',
    shipments: 'Shipments',
    orders: 'Orders',
    billing: 'Billing / CFDI invoicing',
    reports: 'Reports',
    'clients-hub': 'the Client Hub',
    requisitions: 'Requisitions / deliveries',
    recovery: 'Pallet recoveries',
    damage: 'Pallet damages',
    maintenance: 'Maintenance',
    fleet: 'Fleet / Production Operations',
    epcs: 'EPCs / RFID tags',
  };
  return map[seg] ?? `the ${seg.replace(/-/g, ' ')} page`;
}

/**
 * Ask ActiveID as a right-side drawer, mounted once in AppLayout so it's reachable from
 * anywhere and the conversation persists across navigation and open/close. Streams tokens +
 * live tool chips over the /ai/stream hub. Toggled via the header button or Ctrl/Cmd+K.
 */
export function AskAssistantDrawer() {
  const { t } = useTranslation('ai');
  const isOpen = useAiAssistantStore((s) => s.isOpen);
  const setOpen = useAiAssistantStore((s) => s.setOpen);
  const toggle = useAiAssistantStore((s) => s.toggle);
  const pendingPrompt = useAiAssistantStore((s) => s.pendingPrompt);
  const consumePendingPrompt = useAiAssistantStore((s) => s.consumePendingPrompt);
  const location = useLocation();
  const aiEnabled = useFeature('ai');

  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [streaming, setStreaming] = useState(false);
  const { ask } = useAiStream();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Global toggle shortcut.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (!aiEnabled) return; // no Ctrl/⌘+K when AI is off for this tenant
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle, aiEnabled]);

  useEffect(() => {
    if (isOpen) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, streaming, isOpen]);

  const examples = [t('example1'), t('example2'), t('example3')];

  const updateLastAssistant = (fn: (turn: ChatTurn) => ChatTurn) =>
    setTurns((prev) => {
      const copy = [...prev];
      for (let i = copy.length - 1; i >= 0; i--) {
        if (copy[i].role === 'assistant') {
          copy[i] = fn(copy[i]);
          break;
        }
      }
      return copy;
    });

  const submit = (text: string) => {
    const message = text.trim();
    if (!message || streaming) return;

    setTurns((prev) => [...prev, { role: 'user', text: message }, { role: 'assistant', text: '', tools: [] }]);
    setInput('');
    setStreaming(true);

    const toolSet = new Set<string>();
    void ask(
      { conversationId, message, pageContext: describeRoute(location.pathname) },
      {
        onToken: (chunk) => updateLastAssistant((a) => ({ ...a, text: a.text + chunk })),
        onTool: (name) => {
          toolSet.add(name);
          updateLastAssistant((a) => ({ ...a, tools: Array.from(toolSet) }));
        },
        onDone: (cid) => {
          setConversationId(cid);
          setStreaming(false);
        },
        onError: (msg) => {
          const friendly =
            msg === 'unavailable'
              ? t('errors.disabled')
              : msg === 'error' || msg === 'stream error'
                ? t('errors.generic')
                : msg;
          updateLastAssistant((a) => ({ ...a, text: a.text || friendly }));
          setStreaming(false);
          toast.error(friendly);
        },
      },
    );
  };

  // Keep a live ref so the pending-prompt effect always calls the latest submit closure.
  const submitRef = useRef(submit);
  submitRef.current = submit;

  // Auto-send a queued prompt from openWith() — e.g. the dashboard "Explain with AI" button.
  useEffect(() => {
    if (isOpen && pendingPrompt) {
      const queued = consumePendingPrompt();
      if (queued) submitRef.current(queued);
    }
  }, [isOpen, pendingPrompt, consumePendingPrompt]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  };

  const reset = () => {
    if (streaming) return;
    setTurns([]);
    setConversationId(null);
    setInput('');
  };

  if (!aiEnabled) return null; // AI off for this tenant/deployment → no drawer

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-lg">
        {/* header (pr-12 leaves room for the Sheet's built-in close button) */}
        <div className="flex items-center justify-between border-b px-4 py-3 pr-12">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="text-sm leading-tight">{t('title')}</SheetTitle>
              <SheetDescription className="text-xs">{t('subtitle')}</SheetDescription>
            </div>
          </div>
          {turns.length > 0 && (
            <Button variant="ghost" size="sm" onClick={reset} disabled={streaming} className="h-8 shrink-0">
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              {t('newChat')}
            </Button>
          )}
        </div>

        {/* messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-4 p-4">
            {turns.length === 0 && (
              <div className="flex flex-col items-center gap-4 py-8 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Sparkles className="h-6 w-6" />
                </div>
                <p className="text-sm font-medium">{t('empty.heading')}</p>
                <p className="max-w-xs text-xs text-muted-foreground">{t('empty.body')}</p>
                <div className="flex w-full flex-col gap-2">
                  {examples.map((ex, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => submit(ex)}
                      className="rounded-lg border border-input bg-background px-3 py-2 text-left text-xs transition-colors hover:bg-muted"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {turns.map((turn, i) => {
              const isLast = i === turns.length - 1;
              const showThinking = turn.role === 'assistant' && turn.text === '' && streaming && isLast;
              return (
                <div key={i} className={cn('flex gap-2.5', turn.role === 'user' ? 'flex-row-reverse' : 'flex-row')}>
                  <div
                    className={cn(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                      turn.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
                    )}
                  >
                    {turn.role === 'user' ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                  </div>
                  <div className={cn('flex max-w-[85%] flex-col gap-1.5', turn.role === 'user' ? 'items-end' : 'items-start')}>
                    {turn.tools && turn.tools.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {turn.tools.map((name) => (
                          <Badge key={name} variant="secondary" className="gap-1 text-[10px] font-normal">
                            <Database className="h-2.5 w-2.5" />
                            {name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div
                      className={cn(
                        'whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm',
                        turn.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
                      )}
                    >
                      {showThinking ? (
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {t('thinking')}
                        </span>
                      ) : (
                        <>
                          {turn.text}
                          {turn.role === 'assistant' && isLast && streaming && (
                            <span className="ml-0.5 -mb-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-current" />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* composer */}
        <div className="border-t p-3">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('placeholder')}
              rows={1}
              disabled={streaming}
              className="max-h-32 min-h-[44px] resize-none text-sm"
            />
            <Button
              onClick={() => submit(input)}
              disabled={!input.trim() || streaming}
              size="icon"
              className="h-11 w-11 shrink-0"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="mt-2 text-center text-[10px] text-muted-foreground">{t('disclaimer')}</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
